import { lazy, Suspense, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge, Button, Card, CardContent, Input } from '@werkstatt/ui';
import { Eye, Loader2, RotateCcw, Save } from 'lucide-react';
import api from '../../lib/api';
import { readApiError } from '../../lib/apiError';
import { MessageBody } from '../mailbox/MessageBody';
import type { TemplateDetail, TemplateSummary } from '../../types/mailbox';
import { logger } from '@/utils/logger';

const TinyMCEEditor = lazy(() =>
  import('../../components/TinyMCEEditor').then((m) => ({ default: m.TinyMCEEditor })));

export function TemplateEditor({ readOnly }: { readOnly: boolean }) {
  const queryClient = useQueryClient();
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [preview, setPreview] = useState<{ subject: string; body_html: string } | null>(null);

  const { data: list = [] } = useQuery({
    queryKey: ['mail-templates'],
    queryFn: async () => (await api.get<TemplateSummary[]>('/mailbox/templates')).data,
  });

  useEffect(() => {
    if (!activeKey && list.length > 0) setActiveKey(list[0].key);
  }, [list, activeKey]);

  const { data: detail } = useQuery({
    queryKey: ['mail-template', activeKey],
    queryFn: async () =>
      (await api.get<TemplateDetail>(`/mailbox/templates/${activeKey}`)).data,
    enabled: Boolean(activeKey),
  });

  useEffect(() => {
    if (detail) {
      setSubject(detail.subject);
      setBodyHtml(detail.body_html);
      setPreview(null);
    }
  }, [detail]);

  const saveMutation = useMutation({
    mutationFn: async () =>
      (await api.put(`/mailbox/templates/${activeKey}`, {
        subject, body_html: bodyHtml,
      })).data,
    onSuccess: () => {
      toast.success('Vorlage gespeichert');
      queryClient.invalidateQueries({ queryKey: ['mail-templates'] });
      queryClient.invalidateQueries({ queryKey: ['mail-template', activeKey] });
    },
    onError: (error: unknown) => {
      logger.error('Save template failed', error);
      toast.error(readApiError(error, 'Vorlage konnte nicht gespeichert werden'));
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => (await api.delete(`/mailbox/templates/${activeKey}`)).data,
    onSuccess: () => {
      toast.success('Standardvorlage wiederhergestellt');
      queryClient.invalidateQueries({ queryKey: ['mail-templates'] });
      queryClient.invalidateQueries({ queryKey: ['mail-template', activeKey] });
    },
    onError: (error: unknown) =>
      toast.error(readApiError(error, 'Zurücksetzen fehlgeschlagen')),
  });

  const previewMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/mailbox/templates/${activeKey}/preview`, {
        subject, body_html: bodyHtml,
      })).data as { subject: string; body_html: string },
    onSuccess: (data) => setPreview(data),
    onError: (error: unknown) =>
      toast.error(readApiError(error, 'Vorschau fehlgeschlagen')),
  });

  const insert = (variable: string) => {
    setBodyHtml((current) => `${current}{{ ${variable} }}`);
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
      <nav aria-label="Vorlagen" className="space-y-1">
        {list.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setActiveKey(item.key)}
            aria-current={activeKey === item.key}
            className={`w-full rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-muted ${
              activeKey === item.key ? 'bg-muted font-medium' : ''
            }`}
          >
            <span className="block truncate">{item.label}</span>
            <span className="text-xs text-muted-foreground">
              {item.recipient === 'customer' ? 'an den Kunden' : 'an die Vermietung'}
              {item.is_customized ? ' · angepasst' : ''}
            </span>
          </button>
        ))}
      </nav>

      <Card variant="glass">
        <CardContent className="space-y-4 pt-6">
          {!detail && (
            <div className="flex justify-center py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            </div>
          )}

          {detail && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-base font-semibold">{detail.label}</h3>
                {detail.is_customized && <Badge variant="secondary">angepasst</Badge>}
              </div>

              <div className="space-y-1.5">
                <label htmlFor="tpl-subject" className="text-sm font-medium">Betreff</label>
                <Input
                  id="tpl-subject"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  disabled={readOnly}
                />
                <p className="text-xs text-muted-foreground">
                  Die Referenz <code>{'{{ booking.reference }}'}</code> wird beim
                  Speichern automatisch ergänzt – sie ordnet Kundenantworten dem
                  richtigen Vorgang zu.
                </p>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Inhalt</label>
                {readOnly ? (
                  <MessageBody html={bodyHtml} />
                ) : (
                  <Suspense fallback={<div className="h-[320px] animate-pulse rounded-md bg-muted" />}>
                    <TinyMCEEditor
                      value={bodyHtml}
                      onChange={setBodyHtml}
                      placeholder="E-Mail-Vorlage bearbeiten..."
                      height={320}
                    />
                  </Suspense>
                )}
              </div>

              {!readOnly && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Platzhalter einfügen</p>
                  {Object.entries(detail.variables).map(([group, variables]) => (
                    <div key={group} className="flex flex-wrap items-center gap-1.5">
                      <span className="w-20 shrink-0 text-xs text-muted-foreground">
                        {group}
                      </span>
                      {variables.map((variable) => (
                        <button
                          key={variable}
                          type="button"
                          onClick={() => insert(variable)}
                          className="rounded border border-border px-1.5 py-0.5 font-mono text-[11px] hover:bg-muted"
                        >
                          {variable}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => previewMutation.mutate()}
                  disabled={previewMutation.isPending}
                >
                  <Eye className="mr-1.5 h-4 w-4" aria-hidden />
                  Vorschau
                </Button>
                {!readOnly && (
                  <>
                    <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                      <Save className="mr-1.5 h-4 w-4" aria-hidden />
                      Speichern
                    </Button>
                    {detail.is_customized && (
                      <Button
                        variant="outline"
                        onClick={() => resetMutation.mutate()}
                        disabled={resetMutation.isPending}
                      >
                        <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden />
                        Auf Standard zurücksetzen
                      </Button>
                    )}
                  </>
                )}
              </div>

              {preview && (
                <div className="space-y-2 border-t border-border pt-4">
                  <p className="text-sm">
                    <span className="text-muted-foreground">Betreff: </span>
                    {preview.subject}
                  </p>
                  <MessageBody html={preview.body_html} />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
